import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, NgForm, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataFieldGroup } from 'src/app/shared/datafields/models';
import { GenericModalService, ModalSettingsTemplate } from 'src/app/shared/generic-modal/generic-modal.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { Stakeholder } from 'src/app/stakeholder/models/stakeholder';
import { StakeholderService } from 'src/app/stakeholder/services/stakeholder.service';
import { MonitorGroup } from '../../models/monitor-group';
import { MonitorItem, MonitorItemClass, MonitorItemContainer } from '../../models/monitor-item';
import { MonitorSchedule } from '../../models/monitor-schedule';
import { EmbeddedItemsService } from '../../services/embedded-items.service';
import { MonitorService } from '../../services/monitor.service';
import { MonitorItemEditComponent } from '../monitor-item-edit/monitor-item-edit.component';
import { AbsenceItemTemplate, Weekdays } from "../../../shared/scheduling/models/";
import { MonitorAbsenceDialogComponent, MonitorAbsenceDialogResult } from "../monitor-absence-dialog/monitor-absence-dialog.component";
import { LoadingPanelService } from '../../../shared/loading-panel/loading-panel.service';
import { SvgIconComponent } from '../../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';


@Component({
    selector: 'app-monitor-item',
    templateUrl: './monitor-item.component.html',
    styleUrls: ['./monitor-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, FormsModule, ReactiveFormsModule, MonitorItemEditComponent]
})
export class MonitorItemComponent implements OnInit, OnDestroy {
    @ViewChild('theForm') private theForm: NgForm;
    @ViewChild(MonitorItemEditComponent) private monitorItemEdit: MonitorItemEditComponent = null;

    public vm = {
        editing: false,
        monitorItem: { id: 0 } as MonitorItem,
        monitorGroups: [] as MonitorGroup[],
        stakeholders: [] as Stakeholder[],
        monitorSchedules: [] as MonitorSchedule[],
    };

    readonly formData: UntypedFormGroup = null;

    routeSubscription: Subscription;

    constructor(
        fb: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private modalService: GenericModalService,
        private toastService: ToastService,
        private monitorService: MonitorService,
        private stakeholderService: StakeholderService,
        private embeddedItemsService: EmbeddedItemsService,
        private loadingPanelService: LoadingPanelService
    ) {
        this.formData = fb.group({
            monitorItem: [null],
        });

        this.formData.disable();
    }

    ngOnInit(): void {
        this.routeSubscription = this.route.paramMap.pipe(
            map(paramMap => Number(paramMap.get('id')))
        ).subscribe(async id => {
            await this.refresh(id);

            if (!id) {
                this.edit();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
    }

    async refresh(id?: number): Promise<void> {
        id ??= this.vm.monitorItem?.id;

        [
            this.vm.monitorItem,
            this.vm.monitorGroups,
            this.vm.monitorSchedules,
            this.vm.stakeholders
        ] = await Promise.all([
            this.getMonitorItemWithDefault(id),
            this.monitorService.getMonitorGroups(),
            this.monitorService.getMonitorSchedules(),
            this.stakeholderService.getStakeholders()
        ]);

        this.setFormData(this.vm.monitorItem);
    }

    setFormData(monitorItem: MonitorItem) {
        this.theForm.reset();
        this.theForm.resetForm();
        this.formData.setValue({ monitorItem });
    }

    async getMonitorItemWithDefault(id?: number): Promise<MonitorItem> {
        const item = new MonitorItemClass(id ? await this.monitorService.getMonitorItem(id) : undefined);
        return item;
    }

    async edit(): Promise<void> {
        this.vm.editing = true;
        this.formData.enable();
    }

    canDeactivate(): Promise<boolean> | boolean {
        return this.askCanDeactivate();
    }

    async askCanDeactivate(): Promise<boolean> {
        if (!this.vm.editing) {
            return true;
        }
        try {
            await this.modalService.confirmDirect('Not saved', 'You have not saved your changes. Continue?');
            return true;
        }
        catch {
            return false;
        }
    }

    get canSave(): boolean {
        return this.formData.valid;
    }

    async save(): Promise<void> {
        const item: MonitorItemContainer = {
            monitorItem: this.formData.get('monitorItem').value,
            configValues: DataFieldGroup.getDataFieldValues(this.monitorItemEdit.artifactConfigDataFieldGroups, false)
        };

        try {
            this.vm.editing = false;
            this.formData.disable();
            this.vm.monitorItem = await this.monitorService.saveMonitorItem(item);
            this.setFormData(this.vm.monitorItem);

            this.toastService.showToast('Monitor item saved...', ToastType.success);
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save monitor item...', ToastType.error);
        }

        if (item.monitorItem.id !== this.vm.monitorItem.id) {
            // When save returns a new id, i.e. after add new item
            await this.goToItem(this.vm.monitorItem.id);
        }
    }

    async cancel(): Promise<void> {
        this.vm.editing = false;
        this.setFormData(this.vm.monitorItem);
        this.formData.disable();


        if (!this.vm.monitorItem?.id) {
            // When there is no id, we must be canceling an add new operation
            await this.goToItems();
        }
        else {
            // Wish there were a way to incorporate this into the monitorItemEdit instead of calling it here!
            setTimeout(() => this.monitorItemEdit.refreshMonitorItemDataFieldGroups(this.vm.monitorItem.artifactId));
        }
    }

    addNewItem(): void {
        this.goToItem(0);
    }

    async deleteItem(id: number): Promise<void> {
        try {
            await this.modalService.confirmDirect('Delete', 'Are you sure you want to delete this monitor item?');

            try {
                await this.monitorService.deleteMonitorItem(id);
                this.toastService.showToast('Monitor item deleted...', ToastType.success);
                await this.goToItems();
            }
            catch (err) {
                console.error(err);
                this.toastService.showToast('Failed to delete monitor item...', ToastType.error);
            }
        }
        catch (err) {
            // Confirm rejected
        }
    }

    async pauseMonitor(): Promise<void> {
        try {
            const modalSettings = new ModalSettingsTemplate(null, 'static');
            const result: MonitorAbsenceDialogResult = await this.modalService.openComponent(MonitorAbsenceDialogComponent, null, modalSettings);
            if (result !== undefined) {
                let dateTimeNow = new Date();
                let datetimeTo = new Date(result.toDate);
                const fixedTemplate: AbsenceItemTemplate = {
                    Id: null,
                    Name: 'Monitor Pause: ' + this.vm.monitorItem.name,
                    From: dateTimeNow,
                    To: datetimeTo,
                    Note: result.description,
                    IsGlobal: false,
                    Weekdays: Weekdays.Monday | Weekdays.Tuesday | Weekdays.Wednesday | Weekdays.Thursday | Weekdays.Friday | Weekdays.Saturday | Weekdays.Sunday
                };
                this.vm.monitorItem.absenceItemTemplate = fixedTemplate;

                const item: MonitorItemContainer = {
                    monitorItem: this.vm.monitorItem,
                    configValues: null
                };
                this.loadingPanelService.showLoadingPanelDirect('Pausing monitor...');
                await this.monitorService.saveMonitorItem(item);
                this.refresh();
                this.loadingPanelService.hideLoadingPanel();
            }
        }
        catch (err) {
            this.modalService.messageDirect('Exception', 'Error occured while pausing monitor').catch(() => { });
        }
    }


    async resumeMonitor(): Promise<void> {
        try {

            this.vm.monitorItem.absenceItemTemplate = null;

            const item: MonitorItemContainer = {
                monitorItem: this.vm.monitorItem,
                configValues: null
            };
            this.loadingPanelService.showLoadingPanelDirect('Resuming monitor...');
            await this.monitorService.saveMonitorItem(item);
            this.refresh();
            this.loadingPanelService.hideLoadingPanel();
        }
        catch (err) {
            this.modalService.messageDirect('Exception', 'Error occured while pausing monitor').catch(() => { });
        }
    }

    getAbsenceExpireDate(): string {
        let returnDate = "";
        if (this.vm.monitorItem.absenceItemTemplate?.To) {
            const toDate = this.vm.monitorItem.absenceItemTemplate.To.toString();
            returnDate = `${toDate.substring(0, 10)} : ${toDate.substring(11, 16)}`;
        }
        return returnDate;
    }


    async goToItem(id: number): Promise<boolean> {
        return await this.router.navigate(['monitor/items/item', { id }]);
    }

    async goToItems(): Promise<boolean> {
        return await this.router.navigate(['monitor/items']);
    }

    getButtonLabel(monitorItem: MonitorItem): string {
        return this.embeddedItemsService.getButtonLabelForEmbeddedItem(monitorItem.embeddedTypeArea);
    }

    canGotoEmbeddedItem(monitorItem: MonitorItem): boolean {
        return (monitorItem.embeddedTypeId > 0) && this.embeddedItemsService.canNavigateToEmbeddedItem(monitorItem.embeddedTypeArea);
    }

    async gotoEmbeddedItem(monitorItem: MonitorItem): Promise<void> {
        return await this.embeddedItemsService.navigateToEmbeddedItem(monitorItem.embeddedTypeArea, monitorItem.embeddedTypeId);
    }
}
